import { Button } from '../button';
import { Card } from '../card';
import {
  Archive,
  BellIcon,
  BellOff,
  Bookmark,
  CircleIcon,
  EditIcon,
  ExternalLink,
  Link2,
  LockKeyhole,
  Megaphone,
  MessageCircleIcon,
  MoreHorizontal,
  MoreHorizontalIcon,
  ThumbsUpIcon,
  Trash2,
  UsersRoundIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu';
import { SidebarMenuButton } from '../sidebar';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { IPost } from '@/types';
import { Avatar, AvatarFallback, AvatarImage } from '../avatar';
import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';
import { Key, useRef, useState } from 'react';
import PostLikersDialog from './PostLikersDialog';
import { formatTimeAgo } from '@/utils/dates';
import { Share } from '@/components/share-ui';
import PromotePostDialog from './PromotePostDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../alert-dialog';
import { toast } from 'sonner';
import { SomethingWentWrong } from '@/constants/errorMessages';
import CreatePostDialog from '@/components/Forum/Posts/CreatePostDialog';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { Dialog, DialogTrigger } from '../dialog';
import { useJoinCommunityMutation } from '@/api/mutations/communitySubscriptions';
import { usePostFlagMutation } from '@/api/mutations/flagMutations';
import FlagModal from '@/components/ui/FlagModal';
import { useReturnFocus } from '@/hooks/use-return-focus';
import { useVotePostsMutation } from '@/api/mutations/useVotePostsMutation';

import {
  renderPostHtml,
} from '@/components/tiptap-post-input/index';
import { getSiteOrigin } from '@/lib/site-origin';
import { usePromotedPostIds } from '@/api/usePromotedPosts';
import { ShareEntityType } from '@/api/types/shareEntityType';
import PostAttachment from './PostAttachement';
import PostAttachmentsCarousel from './PostAttachmentsCarousel';
import {
  useVotePollMutation,
  useRemovePollVoteMutation,
  useClosePollMutation,
} from '@/api/mutations/usePollVote';
import { getSourcePath } from '@/utils/postSourceHelper';
import { SourceType } from '@/constants/postSource';
import { capitalizeWords } from '@/utils/capitalizeWords';
import {
  EmbedInfo,
  extractUrls,
  getEmbedInfo,
  toURL,
} from '@/utils/mediaEmbed';
import { useIsPublic } from '@/context/PublicMode';
import { PublicSignUpDialog } from '@/components/public/PublicSignupDialog';
import { UserRole } from '@/constants/UserRole';
import { usePinPostMutation } from '@/api/mutations/usePinPostMutation';
import { Check, Pin, PinOff } from 'lucide-react';

interface Props {
  post: IPost;
  className?: string;
  disabled?: boolean;
  showGroupInfo?: boolean;
  showFullContent?: boolean;
  pinnedPostID?: string | null;
  isGroupAdmin?:boolean;
}

/** Produce a nice label like "example.com/path…" */
function prettyLabel(raw: string, max = 38): string {
  const u = toURL(raw);
  if (!u) return raw;
  const host = u.hostname.replace(/^www\./, '');
  const path = (u.pathname + u.search).replace(/\/$/, '');
  const label = path && path !== '/' ? `${host}${path}` : host;
  return label.length > max ? label.slice(0, max - 1) + '…' : label;
}

export default function Post({
  post,
  showGroupInfo = true,
  className,
  showFullContent = false,
  pinnedPostID,
  isGroupAdmin ,
}: Props) {
  const previewLength = 800;
  const previewContent = !showFullContent
    ? (() => {
        const cleanText = post.content
          .replace(/<\/?p>/g, ' ') // remove <p> and </p>
          .replace(/\s+/g, ' ') // collapse spaces
          .trim();

        return cleanText.length > previewLength
          ? cleanText.slice(0, previewLength) + '...'
          : cleanText;
      })()
    : post.content;

  const { user, idTokenPayload } = useAuth();
  const roleIsBusiness = idTokenPayload?.['custom:role'] === '1';

  // toISOString() throws on an unparseable date, which would take down the
  // whole post; fall back to omitting the attribute instead.
  const postDateMs = Date.parse(post.createdAt as string);
  const postDateISO = Number.isNaN(postDateMs)
    ? undefined
    : new Date(postDateMs).toISOString();
  // A promoted post must not offer the button again: the API rejects a repeat
  // promote, so the click could only ever produce an error. The API does not
  // return isPromoted on feed rows, so the answer comes from the user's own
  // promoted-posts list.
  const { data: promotedPostIds } = usePromotedPostIds();
  const isPostPromoted = post.isPromoted || !!promotedPostIds?.has(post.id);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const joinCommunityMutation = useJoinCommunityMutation(post.groupID);
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showLikers, setShowLikers] = useState(false);
  const [archivePostDialogOpen, setArchivePostDialogOpen] = useState(false);
  const [closePostDialogOpen, setClosePostDialogOpen] = useState(false);

  const [dialogKey, setDialogKey] = useState('1');
  const [menuOpen, setMenuOpen] = useState(false);
  const pinPostMutation = usePinPostMutation();
  const isPinned = pinnedPostID === post.id;
  const votePostMutation = useVotePostsMutation();
  const displayName = post?.source?.sourceName || post?.group?.name;
  const sourcePath = getSourcePath({
    id: post.source?.sourceID || post.group?.id,
    type: (post.source?.sourceType as SourceType) || undefined,
  });
  // Flag mutation and modal state
  const flagMutation = usePostFlagMutation();
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const postOptionsRef = useRef<HTMLButtonElement | null>(null);
  const menuFocus = useReturnFocus();
  const restoreFocus = () =>
    menuFocus.restore(document.querySelector('main'));
  const router = useRouter();

  // Optimistic state for immediate UI updates
  const [optimisticVote, setOptimisticVote] = useState<1 | -1 | 0 | null>(null);
  const [optimisticUpvoteCount, setOptimisticUpvoteCount] = useState<
    number | null
  >(null);
  const [optimisticIsReported, setOptimisticIsReported] = useState<
    boolean | null
  >(null);

  const isPublic = useIsPublic();
  const PostTitleTag = showFullContent ? 'h1' : 'h2';
  // Compute display values with optimistic updates
  const displayVote = optimisticVote !== null ? optimisticVote : post.vote;
  const displayUpvoteCount =
    optimisticUpvoteCount !== null ? optimisticUpvoteCount : post.upvoteCount;
  const displayIsReported =
    optimisticIsReported !== null ? optimisticIsReported : post.isReported;

  const votePollMutation = useVotePollMutation();
  const removePollVoteMutation = useRemovePollVoteMutation();
  const closePollMutation = useClosePollMutation();
  const isPollBusy =
    votePollMutation.isPending || removePollVoteMutation.isPending;

  // While the poll is open a vote is never final: tapping the chosen option
  // again removes it, tapping another moves it (remove, then vote — the API
  // has no "change vote", and only the final step refreshes and toasts).
  const handlePollVote = async (id: string) => {
    const current = post.userChoiceID || null;
    if (current === id) {
      removePollVoteMutation.mutate({ postId: post.id });
      return;
    }
    if (current) {
      try {
        await removePollVoteMutation.mutateAsync({
          postId: post.id,
          silent: true,
        });
      } catch {
        return; // already reported by the mutation's onError
      }
      votePollMutation.mutate({ postId: post.id, choiceID: id, replacing: true });
      return;
    }
    votePollMutation.mutate({ postId: post.id, choiceID: id });
  };
  const goToPostDetailsPage = () => {
    if (isPublic) return;
    router.push(`/communities/${post.groupID}/posts/${post.id}`);
  };
  /**
   * 
  const wrapContent = (content: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    let processed = content.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer"> ${url} </a>`;
    });

    if (typeof wrapHashtagsInAnchor === 'function') {
      processed = wrapHashtagsInAnchor(processed);
    }

    return processed;
  };
   */

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('a')) {
      return;
    }
    goToPostDetailsPage();
  };

  const closeMenu = async () => {
    setMenuOpen(false);
    await queryClient.invalidateQueries({ queryKey: ['posts', post.id] });
    // Bumping the key remounts the edit dialog so it reopens with fresh state.
    // Radix keeps the content mounted for its 200ms exit animation, and it only
    // releases the body's pointer-events lock when that teardown finishes — so
    // wait it out rather than yanking the node mid-close.
    setTimeout(() => {
      setDialogKey((prev) => String(Number(prev) + 1));
    }, 250);
  };

  const handleConfirmDeletePost = async () => {
    try {
      await Axios.delete(`/posts/${post.id}`);
      toast.success('Post deleted successfully! ');
      await queryClient.invalidateQueries({
        queryKey: ['community', post.groupID, 'posts'],
      });
      await queryClient.invalidateQueries({ queryKey: [`posts/feed`] });
      await queryClient.invalidateQueries({ queryKey: [`posts/archived`] });
      router.refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? SomethingWentWrong);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleConfirmArchivePost = async () => {
    try {
      await Axios.put(`/posts/${post.id}/archive`, {});
      toast.success('Post archived successfully!');
      await queryClient.invalidateQueries({
        queryKey: ['community', post.groupID, 'posts'],
      });
      await queryClient.invalidateQueries({ queryKey: ['posts', post.id] });
      await queryClient.invalidateQueries({ queryKey: [`posts/archived`] });
      // The forums feed is server-rendered, so the archived post only drops out
      // of it on a refresh — same as the delete path above.
      router.refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? SomethingWentWrong);
    } finally {
      setArchivePostDialogOpen(false);
    }
  };

  const handleBookmarkToggle = async () => {
    try {
      if (post.isBookmarked) {
        await Axios.delete(`/posts/bookmarks`, {
          data: { postID: post.id },
        });
        toast.success('Post removed from bookmarks!');
      } else {
        await Axios.post(`/posts/bookmarks`, { postID: post.id });
        toast.success('Post saved successfully!');
      }
      await queryClient.invalidateQueries({
        queryKey: ['community', post.groupID, 'posts'],
      });
      await queryClient.invalidateQueries({ queryKey: ['posts', post.id] });
      router.refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Something went wrong!');
    }
  };

  const handleConfirmClosePost = async () => {
    try {
      await Axios.put(`/posts/${post.id}/close`, {});
      toast.success('Discussion closed successfully!');
      await queryClient.invalidateQueries({
        queryKey: ['community', post.groupID, 'posts'],
      });
      await queryClient.invalidateQueries({ queryKey: ['posts', post.id] });
      router.refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? SomethingWentWrong);
    } finally {
      setClosePostDialogOpen(false);
    }
  };

  const alerts = (
    <>
      <PostLikersDialog
        postID={post.id}
        isOpen={showLikers}
        onOpenChange={setShowLikers}
      />
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          // Without onOpenChange these dialogs ignored Escape entirely.
          setDeleteDialogOpen(open);
          if (!open) restoreFocus();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete the post{' '}
              <span className="text-black font-bold">{`"${post.title}"`}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeletePost}
              className="bg-destructive text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={closePostDialogOpen}
        onOpenChange={(open) => {
          // Without onOpenChange these dialogs ignored Escape entirely.
          setClosePostDialogOpen(open);
          if (!open) restoreFocus();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Close the discussion for{' '}
              <span className="text-black font-bold">{`"${post.title}"`}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setClosePostDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClosePost}
              className="bg-destructive text-white"
            >
              Close Discussion
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={archivePostDialogOpen}
        onOpenChange={(open) => {
          // Without onOpenChange these dialogs ignored Escape entirely.
          setArchivePostDialogOpen(open);
          if (!open) restoreFocus();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {post.isArchived ? 'Unarchive' : 'Archive'} the post{' '}
              <span className="text-black font-bold">{`"${post.title}"`}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setArchivePostDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmArchivePost}
              className="bg-destructive text-white"
            >
              {post.isArchived ? 'Unarchive' : 'Archive'} Post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  const handleFlagClick = () => {
    setFlagModalOpen(true);
  };

  const handleFlagSubmit = (reason: string) => {
    // Optimistically update flag state
    setOptimisticIsReported(true);
    setFlagModalOpen(false);

    flagMutation.mutate(
      {
        postID: post.id,
        reason,
      },
      {
        onSuccess: () => {
          // The mutation will automatically invalidate queries and refetch data
        },
        onError: () => {
          // Revert optimistic update on error
          setOptimisticIsReported(null);
        },
      }
    );
  };
  const MenuItem = ({
    icon,
    title,
    description,
    onClick,
    onSelect,
    disabled = false,
  }: {
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick?: () => void;
    onSelect?: (e: Event) => void;
    disabled?: boolean;
  }) => (
    <DropdownMenuItem
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      onSelect={!disabled ? onSelect : undefined}
      className={cn(
        'flex items-center gap-3 rounded-xl px-3',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer hover:bg-[#eae3dc] focus:bg-[#eae3dc]'
      )}
    >
      <div className="text-primary shrink-0">{icon}</div>

      <div className="flex flex-col justify-center">
        <span className="font-medium text-sm text-black">{title}</span>
        {/* `text-primary` (#948780) is only 3.48:1 on the menu background —
            fine for the icon above, not for text. */}
        <span className="text-xs text-[#554E49]">{description}</span>
      </div>
    </DropdownMenuItem>
  );
  const handleCardClick = (e: any) => {
    const target = e.target as HTMLElement;
    // Skip navigation for clicks on interactive controls — let their own
    // handlers run (vote, join, comment, share, dropdown items, etc.).
    if (
      target.closest('a') ||
      target.closest('button') ||
      target.closest('[role="button"]')
    ) {
      return;
    }
    if (isPublic) {
      setShowLoginDialog(true);
      return;
    }
    goToPostDetailsPage();
  };

  const handleFooterClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // Ignore clicks on interactive controls so default action is preserved.
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('svg') ||
      target.closest('[role="button"]')
    ) {
      return;
    }

    if (isPublic) {
      setShowLoginDialog(true);
      return;
    }

    goToPostDetailsPage();
  };

  // Public forum posts come back without an author name, so everything that
  // reads the name — initials, the avatar label, the byline — goes through one
  // fallback instead of each guarding (or not) on its own.
  const authorName = post.fullName?.trim() || 'Wee Amici user';
  const initials = authorName
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
  // Public posts show a single letter; signed-in members get full initials.
  const userInitials = isPublic ? initials.slice(0, 1) : initials;
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  return (
    <Card
      className={cn(
        'p-2 w-full sm:w-[91%] min-w-0 max-w-full overflow-hidden flex gap-0 relative shadow-none border-0 cursor-pointer',
        !showFullContent && 'hover:bg-[#e7e3db] rounded-md transition-colors',
        className
      )}
      onClick={handleCardClick}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <PublicSignUpDialog
          open={showLoginDialog}
          onOpenChange={setShowLoginDialog}
          message="Sign up to read this post and join the discussion!"
        />
      </div>
      <div onClick={(e) => e.stopPropagation()}>{alerts}</div>
      <div className="flex items-start sm:items-center gap-2 sm:gap-3 mb-3">
        {/* alt was post.userID, so this announced as a raw UUID. The name comes
            from the link rather than the image so it survives the avatar
            falling back to initials when the picture fails to load. */}
        <Link
          href={isPublic ? `` : `/profile/${post.userID}`}
          className="shrink-0"
          aria-label={`${authorName} profile picture`}
        >
          <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
            <AvatarImage
              className={cn(isPublic && 'blur-[1px] pointer-events-none')}
              src={
                user?.sub === post.userID
                  ? user.picture
                  : post.profilePictureURL
              }
              alt=""
            />
            <AvatarFallback className="rounded-lg">
              {userInitials}
            </AvatarFallback>
          </Avatar>
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 flex-1 min-w-0 leading-tight">
          <div className="flex items-center min-w-0 sm:gap-2">
            <Link
              href={isPublic ? `` : `/profile/${post.userID}`}
              className="min-w-0"
            >
              <p className="font-bold text-sm sm:text-base 2xl:text-lg mr-2 hover:text-primary truncate">
                {authorName}
              </p>
            </Link>
            <CircleIcon
              aria-hidden="true"
              className="hidden sm:inline-block fill-primary  text-primary opacity-70 shrink-0 h-2.5 w-2.5"
            />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 mt-0.5 sm:mt-0 text-xs sm:text-base 2xl:text-lg text-[#554E49]">
            <time
              // Emitted whenever the date is real, public or not: it is the
              // machine-readable date crawlers and answer engines read, and
              // the public forum now shows genuine posts.
              dateTime={postDateISO}
              className="whitespace-nowrap shrink-0 font-semibold sm:font-bold"
            >
              <span className="sr-only">Posted </span>
              {/* Sample posts carry a pre-baked label ("2 hrs ago"); real
                  posts only have createdAt. Preferring the label keeps the
                  curated preview intact while real posts show a real date —
                  before this, public posts rendered no date at all. */}
              {post.createdAtPostSample ?? formatTimeAgo(post.createdAt)}
            </time>
            {displayName && (
              <>
                <CircleIcon
                  aria-hidden="true"
                  className="fill-current sm:fill-primary sm:text-primary opacity-70 shrink-0 h-1.5 w-1.5 sm:h-2 sm:w-2"
                />
                <Link
                  href={isPublic ? `` : sourcePath}
                  className="min-w-0 hover:text-primary"
                >
                  <span className="font-semibold sm:font-bold truncate block">
                    {capitalizeWords(displayName)}
                  </span>
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {/* text-primary on a 10% tint of itself is 2.57:1. The tint stays —
              it is the badge's identity — and only the text darkens, to
              6.03:1. */}
          {isPostPromoted && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs sm:text-sm font-semibold text-[#554E49]">
              <Megaphone className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Promoted
            </span>
          )}
          {(user?.sub === post.userID || user?.role == UserRole.ADMIN) ? (
            <Dialog>
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    ref={postOptionsRef}
                    size="lg"
                    aria-label="Post options"
                    className="cursor-pointer p-0 h-8 w-8 flex rounded-lg justify-center"
                  >
                    <MoreHorizontalIcon aria-hidden="true" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="min-w-56 border-1 rounded-3xl"
                  side="bottom"
                  align="end"
                  sideOffset={4}
                  collisionPadding={8}
                >
                  {user.sub === post.userID && (
                    <>
                      <MenuItem
                        icon={
                          post.isBookmarked ? (
                            <BellOff className="size-5 fill-primary text-primary" />
                          ) : (
                            <BellIcon className="size-5 fill-primary text-primary" />
                          )
                        }
                        title={
                          post.isBookmarked
                            ? 'Turn off notifications'
                            : 'Turn on notifications'
                        }
                        description={
                          post.isBookmarked
                            ? 'Don’t show notifications for this post'
                            : 'show notifications for this post'
                        }
                        onClick={() => {
                          closeMenu();
                        }}
                      />
                      <MenuItem
                        icon={
                          <Bookmark
                            className={cn(
                              'size-5',
                              post.isBookmarked ? 'fill-primary' : 'text-primary'
                            )}
                          />
                        }
                        title={post.isBookmarked ? 'UnBookmark' : 'Bookmark'}
                        description={
                          post.isBookmarked
                            ? 'Remove this post from bookmarks'
                            : 'Save this post for later'
                        }
                        onClick={handleBookmarkToggle}
                      />
                    </>
                  )}

                  <CreatePostDialog
                    key={dialogKey}
                    post={post}
                    onSuccess={closeMenu}
                    trigger={
                      <>
                        {!post.isArchived && post.postType !== 'poll' && (
                          <CreatePostDialog
                            key={dialogKey}
                            post={post}
                            onSuccess={closeMenu}
                            onClosed={() => {
                              // This dialog opens from a menu item, which the
                              // closing menu unmounts — so Radix has nothing
                              // left to hand focus back to.
                              setMenuOpen(false);
                              restoreFocus();
                            }}
                            trigger={
                              <DialogTrigger asChild>
                                <MenuItem
                                  icon={
                                    <EditIcon className="size-5 text-primary" />
                                  }
                                  title="Edit"
                                  description="Update the content of your post"
                                  onSelect={(e) => {
                                    e.preventDefault();
                                    menuFocus.capture(postOptionsRef.current);
                                  }}
                                />
                              </DialogTrigger>
                            }
                          />
                        )}
                      </>
                    }
                  />
                  {isGroupAdmin && pinnedPostID !== undefined && (
                    <MenuItem
                      icon={
                        isPinned ? (
                          <PinOff className="size-5 text-primary" />
                        ) : (
                          <Pin className="size-5 text-primary" />
                        )
                      }
                      title={isPinned ? 'Unpin post' : 'Pin post'}
                      description={
                        isPinned
                          ? 'Remove this post from the top'
                          : 'Pin this post to the top of the group'
                      }
                      onClick={() => {
                        pinPostMutation.mutate({
                          groupID: post.groupID,
                          postID: post.id,
                          isPinned,
                        });
                        closeMenu();
                      }}
                    />
                  )}
                  <MenuItem
                    icon={<Trash2 className="size-5 text-primary" />}
                    title="Delete"
                    description="Permanently remove post "
                    onClick={() => {
                      menuFocus.capture(postOptionsRef.current);
                      setDeleteDialogOpen(true);
                    }}
                  />
                  {user.sub === post.userID && (
                    <>
                      <MenuItem
                        icon={<Archive className="size-5 text-primary" />}
                        title={post.isArchived ? 'Restore post' : 'Archive post'}
                        description={
                          post.isArchived
                            ? 'Make this post visible again'
                            : 'Hide this post from the feed'
                        }
                        onClick={() => {
                          menuFocus.capture(postOptionsRef.current);
                          setArchivePostDialogOpen(true);
                        }}
                      />
                      <MenuItem
                        icon={<LockKeyhole className="size-5 text-primary" />}
                        title="Close discussion"
                        disabled={post.isClosed}
                        description="Don’t allow members to comment"
                        onClick={() => {
                          menuFocus.capture(postOptionsRef.current);
                          setClosePostDialogOpen(true);
                        }}
                      />
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </Dialog>
          ) : !isPublic ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  ref={postOptionsRef}
                  variant="ghost"
                  size="sm"
                  aria-label="Post options"
                  className="h-8 w-8 p-0 hover:bg-transparent"
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={
                    displayIsReported
                      ? undefined
                      : () => {
                          menuFocus.capture(postOptionsRef.current);
                          handleFlagClick();
                        }
                  }
                  disabled={displayIsReported}
                  className={
                    displayIsReported ? 'text-red-500 cursor-not-allowed' : ''
                  }
                >
                  {displayIsReported ? 'Already Flagged' : 'Flag Post'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>
      <Link
        href={
          isPublic
            ? ``
            : `/communities/${post.groupID}/posts/${post.id}`
        }
      >
        {/* On the detail page this post IS the page, so its title is the h1.
            In a feed it is one of many, sitting under the page's own heading,
            so it stays an h2. Without this the detail page had no h1 at all
            and a screen reader had nothing to anchor the outline to. */}
        <PostTitleTag className="font-bold text-lg sm:text-2xl md:text-3xl 2xl:text-4xl mb-2 sm:mb-3 mt-1 break-words">
          {post.title}
        </PostTitleTag>
      </Link>
      {post.postType === 'poll' && post.pollChoices ? (
        <div
          className="space-y-3 mb-4 mt-2 relative"
          style={{
            contain: 'paint',
            overflow: 'hidden',
          }}
        >
          {post.pollChoices.map((option) => {
            const percentage = option.percentage ?? 0;
            const isInteractionDisabled = !!post.closedAt;
            const isUsersChoice = post.userChoiceID === option.id;

            return (
              <div key={option.id} className="relative group">
                <Button
                  variant="outline"
                  disabled={isInteractionDisabled || isPollBusy}
                  aria-pressed={isUsersChoice}
                  aria-label={
                    isUsersChoice
                      ? `${option.text}, your vote — tap to remove`
                      : undefined
                  }
                  className={cn(
                    'w-full h-12 justify-between px-4 border-[#cdc4c1] rounded-full relative overflow-hidden transition-all',
                    isInteractionDisabled && 'cursor-default opacity-90',
                    isUsersChoice && 'border-[#786D67] border-2'
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isInteractionDisabled && !isPublic)
                      handlePollVote(option.id);
                    if (isPublic) {
                      handleCardClick(e);
                    }
                  }}
                >
                  <div className="flex items-center gap-2 z-10">
                    {isUsersChoice && (
                      <Check
                        className="h-4 w-4 shrink-0"
                        strokeWidth={3}
                        aria-hidden="true"
                      />
                    )}
                    <span className={cn('font-medium')}>{option.text}</span>
                  </div>
                  <span className="text-sm font-bold z-10">{percentage}%</span>

                  <div
                    className={cn(
                      'absolute left-0 top-0 h-full transition-all duration-500',
                      'bg-[#cdc4c1]'
                    )}
                    style={{ width: `${percentage}%` }}
                  />
                </Button>
              </div>
            );
          })}

          {/* Poll Footer: Info & Close Button */}
          <div className="flex items-center justify-between px-2 mt-2">
            <p className="text-sm text-muted-foreground font-medium">
              {post.closedAt
                ? 'Poll Closed'
                : post.userChoiceID
                  ? 'You voted · tap your choice to remove it, or pick another to change'
                  : ''}
            </p>

            {/* Show Close Poll only to Owner if poll is still open */}
            {user?.sub === post.userID && !post.closedAt && (
              <Button
                variant="ghost"
                size="sm"
                // red-500 gives white text only 3.81:1; red-600 clears 4.5.
                className="text-white rounded-full bg-red-600 hover:bg-red-700"
                onClick={(e) => {
                  e.stopPropagation();
                  closePollMutation.mutate(post.id);
                  router.refresh();
                }}
              >
                Close Poll
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          className="text-gray-800 text-sm sm:text-lg 2xl:text-2xl mb-3 break-words [&_.mention]:bg-[#bbae91] [&_.mention]:font-bold [&_.mention]:rounded-md [&_.mention]:p-1"
          dangerouslySetInnerHTML={{
            __html: renderPostHtml(previewContent),
          }}
          onClick={handleContainerClick}
        />
      )}
      {(() => {
        const carouselItems: string[] =
          post.attachments && post.attachments.length > 0
            ? (post.attachments as string[])
            : post.attachment
              ? [post.attachment]
              : [];
        const hasMedia = post.media && post.media.length > 0;
        return carouselItems.length > 0 || hasMedia ? (
          <PostAttachmentsCarousel
            attachments={carouselItems}
            media={post.media}
          />
        ) : null;
      })()}
      {(() => {
        const managerUrls: string[] = Array.isArray(post.urls) ? post.urls : [];
        const contentUrls = extractUrls(post.content ?? '');
        const seenEmbeds = new Set<string>();
        const embeds: { raw: string; info: EmbedInfo }[] = [];
        for (const raw of [...managerUrls, ...contentUrls]) {
          const info = getEmbedInfo(raw);
          if (!info) continue;
          if (seenEmbeds.has(info.url)) continue;
          seenEmbeds.add(info.url);
          embeds.push({ raw, info });
        }
        const otherUrls = managerUrls.filter(
          (raw: string) => !getEmbedInfo(raw)
        );
        if (embeds.length === 0 && otherUrls.length === 0) return null;
        return (
          <section className="mt-2 mb-4 space-y-3">
            {embeds.length > 0 && (
              <div className="space-y-2">
                {embeds.map(
                  (
                    e: { raw: string; info: EmbedInfo },
                    idx: number
                  ) => (
                    <div
                      key={`embed-${idx}`}
                      className="relative w-full overflow-hidden rounded-xl"
                      style={{
                        maxWidth: e.info.maxWidth
                          ? `${e.info.maxWidth}px`
                          : undefined,
                        ...(e.info.fixedHeight
                          ? { height: e.info.fixedHeight }
                          : { aspectRatio: e.info.aspect ?? '16 / 9' }),
                        ...(!e.info.maxWidth ? { maxWidth: '36rem' } : {}),
                      }}
                    >
                      <iframe
                        src={e.info.url}
                        title={e.info.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="absolute inset-0 h-full w-full"
                      />
                    </div>
                  )
                )}
              </div>
            )}
            {otherUrls.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-semibold text-muted-foreground">
                    Related links
                  </h4>
                </div>
                <ul className="flex flex-wrap gap-2">
                  {otherUrls.map((raw: string, idx: Key | null | undefined) => {
                    const u = toURL(raw);
                    const href = u ? u.href : raw;
                    const label = prettyLabel(raw);
                    return (
                      <li key={idx}>
                        {/* text-primary (#948780) is a fill colour, not a text
                            colour: 2.82:1 on this card and 3.25:1 once hover
                            swaps in bg-muted, both under the 4.5:1 that 12px
                            text needs. #554E49 is the same brand family and
                            clears it at 6.61:1 / 7.62:1. */}
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-3 py-1 text-xs text-[#554E49] hover:bg-muted transition-colors max-w-[260px]"
                          title={href}
                        >
                          <span className="truncate">{label}</span>
                          <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>
        );
      })()}
      <div className="flex gap-2 flex-wrap" onClick={handleFooterClick}>
        <div className="flex items-center gap-0 sm:gap-2 flex-wrap">
          <Button
            aria-label={displayVote === 1 ? 'Remove your like' : 'Like'}
            aria-pressed={displayVote === 1}
            className={cn(
              'flex items-center shadow-none gap-1 border-none text-[#554E49] text-sm sm:text-base 2xl:text-lg bg-transparent hover:bg-transparent hover:text-[#554E49] px-2 sm:px-3',
              isPublic && 'pointer-events-none'
            )}
            onClick={(e) => {
              // Ensure clicking the share button does not trigger the card click handler
              e.stopPropagation();
              const currentVote = displayVote;
              const newVote = currentVote === 1 ? 0 : 1;

              // Optimistically update vote state and count
              setOptimisticVote(newVote);

              // Calculate new upvote count based on vote transition
              let newUpvoteCount = displayUpvoteCount;
              if (currentVote === 1 && newVote === 0) {
                // Removing an upvote
                newUpvoteCount = Math.max(0, displayUpvoteCount - 1);
              } else if (currentVote !== 1 && newVote === 1) {
                // Adding an upvote (from 0 or -1)
                newUpvoteCount = displayUpvoteCount + 1;
              }

              setOptimisticUpvoteCount(newUpvoteCount);

              votePostMutation.mutate(
                {
                  postId: post.id,
                  vote: newVote,
                  communityId: post.groupID,
                },
                {
                  onError: () => {
                    // Revert optimistic update on error
                    setOptimisticVote(null);
                    setOptimisticUpvoteCount(null);
                  },
                }
              );
            }}
          >
            <ThumbsUpIcon fill={displayVote === 1 ? '#554E49' : 'none'} />
          </Button>
          {/* The count is its own control: the thumb casts the vote, the
              number opens the list of who upvoted. They have to be siblings —
              a button inside a button is invalid and breaks assistive tech. */}
          <Button
            variant="ghost"
            disabled={displayUpvoteCount === 0}
            aria-label={`See who liked this post (${displayUpvoteCount})`}
            onClick={(e) => {
              e.stopPropagation();
              if (displayUpvoteCount === 0) return;
              setShowLikers(true);
            }}
            className={cn(
              'shadow-none border-none bg-transparent text-[#554E49] text-sm sm:text-base 2xl:text-lg hover:bg-[#948780]/15 hover:text-[#554E49] px-1.5 -ml-2 sm:-ml-3',
              displayUpvoteCount === 0 && 'pointer-events-none opacity-100',
              isPublic && 'pointer-events-none'
            )}
          >
            <p>{displayUpvoteCount}</p>
          </Button>
          <Button
            variant="outline"
            disabled={post.isClosed}
            onClick={goToPostDetailsPage}
            // The only text in here is the count, which is empty when the API
            // omits it — leaving the button nameless. The label states the
            // action and still carries the visible number.
            aria-label={`View comments (${post.commentCount ?? 0})`}
            className={cn(
              'flex items-center gap-1 shadow-none border-none bg-transparent text-[#554E49] text-sm sm:text-base 2xl:text-lg hover:bg-transparent hover:text-[#554E49] disabled:opacity-50 disabled:cursor-not-allowed px-2 sm:px-3',
              isPublic && 'pointer-events-none'
            )}
          >
            <MessageCircleIcon />
            <p>{post.commentCount}</p>
          </Button>
          <div
            onClick={(e) => {
              // Ensure clicking the share button does not trigger the card click handler
              e.stopPropagation();
            }}
          >
            <Share
              shareEntityID={post.id}
              buttonColorClasses="shadow-none gap-1 border-none text-[#554E49] 2xl:text-lg bg-transparent hover:bg-transparent hover:text-[#554E49]"
              showOnlyIcon={true}
              showSpanUnderIcon={true}
              shareEntityType={ShareEntityType.POST}
              sharePath={
                getSiteOrigin() +
                `/communities/${post.groupID}/posts/${post.id}`
              }
              isSharingEnabled={false}
            />
          </div>
          {!isPublic &&
            roleIsBusiness &&
            user?.sub === post.userID &&
            !isPostPromoted && (
            <Button
              variant="outline"
              onClick={(e) => {
                // State-driven dialog, so Radix has no trigger to restore to
                // and focus would land on <body> when it closes.
                e.stopPropagation();
                menuFocus.capture(e.currentTarget);
                setPromoteDialogOpen(true);
              }}
              className="flex items-center gap-1 shadow-none border-none bg-transparent text-[#554E49] text-sm sm:text-base 2xl:text-lg hover:bg-transparent hover:text-[#554E49] px-2 sm:px-3"
              title="Promote this post"
            >
              <Megaphone className="!w-6 !h-6 sm:!w-7 sm:!h-7 stroke-[1.6]" />
              <span className="hidden sm:inline">Promote</span>
            </Button>
          )}
          {isPublic || (showGroupInfo && post.groupID && !post.isMember && !post.source )? (
            <Button
              variant="outline"
              className={cn(
                'flex items-center shadow-none gap-1 border-none text-[#554E49] text-sm sm:text-base 2xl:text-lg bg-transparent hover:bg-transparent hover:text-[#554E49] px-2 sm:px-3',
                post.isMember && 'cursor-not-allowed'
              )}
              onClick={() => {
                if (!isPublic && !post.isMember) {
                  joinCommunityMutation.mutate(user?.sub);
                  post.isMember = true;
                }
              }}
            >
              <UsersRoundIcon />
              <span className="hidden sm:inline">Join the Community</span>
              <span className="sm:hidden">Join</span>
            </Button>
          ):<>
          </>}
        </div>
      </div>
      <FlagModal
        isOpen={flagModalOpen}
        onClose={() => {
          setFlagModalOpen(false);
          restoreFocus();
        }}
        onFlag={handleFlagSubmit}
        isLoading={flagMutation.isPending}
        title="Flag Post"
        description="Please provide a reason for flagging this post. This will help our moderation team review the issue."
      />
      <PromotePostDialog
        postID={post.id}
        open={promoteDialogOpen}
        onOpenChange={(open) => {
          setPromoteDialogOpen(open);
          if (!open) restoreFocus();
        }}
      />
    </Card>
  );
}
